import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert, Box, Button, CircularProgress, FormControl, InputLabel, MenuItem, Paper, Select,
    SelectChangeEvent, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { AuthService } from '../../../services/auth.service';
import { useToast } from '../../../context/ToastContext';
import { CommercialReportService } from '../../../services/commercialReport.service';
import { IIncentiveStatusReport, IncentiveClaimStatus } from '../../../interfaces/commercial.interface';
import { dash, money, triggerDownload } from '../../statutoryRegisters/shared';
import { enumLabel } from '../../../utils/garmentsLabels';
import { formatDate, toLocalIsoDate } from '../../../utils/dateUtils';

const CLAIM_STATUSES: IncentiveClaimStatus[] = ['DRAFT', 'SUBMITTED', 'UNDER_AUDIT', 'APPROVED', 'RECEIVED', 'REJECTED'];

/**
 * T11 report 3 — cash-incentive claim status (T8 brief item 3). All amounts BDT (R-27), so unlike
 * the other two reports there is no per-currency sub-total discipline here. R-3: when the module
 * is off the backend returns `enabled=false` with a `disabledReason` and every list field empty —
 * rendered as a banner, never treated as an error/crash.
 */
const IncentiveClaimStatusReport: React.FC = () => {
    const toast = useToast();
    const institutionId = AuthService.getSelectedInstitution()!;
    const [report, setReport] = useState<IIncentiveStatusReport | null>(null);
    const [loading, setLoading] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [statusFilter, setStatusFilter] = useState<IncentiveClaimStatus | 'ALL'>('ALL');

    const load = async () => {
        setLoading(true);
        try {
            const data = await CommercialReportService.getIncentiveClaimStatusReport(institutionId);
            setReport(data);
        } catch (e) {
            setReport(null);
            toast.showApiError(e, 'Failed to load incentive claim status report');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [institutionId]);

    const handleDownload = async () => {
        setDownloading(true);
        try {
            const blob = await CommercialReportService.downloadIncentiveClaimStatusReportXlsx(institutionId);
            triggerDownload(blob, `incentive-claim-status-${toLocalIsoDate()}.xlsx`);
        } catch (e) {
            toast.showApiError(e, 'Download failed');
        } finally {
            setDownloading(false);
        }
    };

    const filteredRows = useMemo(
        () => (report?.rows ?? []).filter(r => statusFilter === 'ALL' || r.status === statusFilter),
        [report, statusFilter]
    );

    return (
        <Box data-help-page="commercialReports.incentive.page">
            <Box data-help-section="commercialReports.incentive.toolbar" display="flex" gap={2} alignItems="flex-end" mb={2} flexWrap="wrap">
                <Button data-help="commercialReports.incentive.refresh" variant="outlined" size="small" onClick={load} disabled={loading}>Refresh</Button>
                <Button data-help="commercialReports.incentive.download"
                    variant="contained" size="small" startIcon={<FileDownloadIcon />}
                    onClick={handleDownload} disabled={!report || !report.enabled || downloading}
                >
                    Download XLSX
                </Button>
                <FormControl data-help="commercialReports.incentive.status" size="small" sx={{ flex: '1 1 180px', maxWidth: 300 }}>
                    <InputLabel id="incentive-status-filter-label">Status</InputLabel>
                    <Select
                        labelId="incentive-status-filter-label"
                        label="Status"
                        value={statusFilter}
                        onChange={(e: SelectChangeEvent) => setStatusFilter(e.target.value as IncentiveClaimStatus | 'ALL')}
                    >
                        <MenuItem value="ALL">All Statuses</MenuItem>
                        {CLAIM_STATUSES.map(s => <MenuItem key={s} value={s}>{enumLabel(s)}</MenuItem>)}
                    </Select>
                </FormControl>
            </Box>

            {loading && <CircularProgress size={24} />}

            {!loading && report && !report.enabled && (
                <Alert severity="info" data-testid="incentive-status-disabled-banner">
                    {report.disabledReason}
                </Alert>
            )}

            {!loading && report && report.enabled && (
                <>
                    {report.truncated && (
                        <Alert severity="warning" sx={{ mb: 2 }} data-testid="incentive-status-truncated">
                            Showing {report.rows.length} of {report.totalMatchingRows} matching claims. Totals below
                            still reflect the full, untruncated dataset.
                        </Alert>
                    )}

                    <Typography variant="h6" sx={{ mb: 1 }}>By Status</Typography>
                    <TableContainer data-help-section="commercialReports.incentive.byStatus" component={Paper} sx={{ mb: 2 }} data-testid="incentive-status-by-status">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell data-help="commercialReports.incentive.col.status">Status</TableCell>
                                    <TableCell data-help="commercialReports.incentive.col.count" align="right">Count</TableCell>
                                    <TableCell data-help="commercialReports.incentive.col.claimed" align="right">Claimed</TableCell>
                                    <TableCell data-help="commercialReports.incentive.col.received" align="right">Received</TableCell>
                                    <TableCell data-help="commercialReports.incentive.col.shortfall" align="right">Shortfall</TableCell>
                                    <TableCell data-help="commercialReports.incentive.col.notYet" align="right">Not Yet Received</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {report.byStatus.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center">
                                            <Typography color="text.secondary">No data.</Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                                {report.byStatus.map((s, i) => (
                                    <TableRow key={`${s.status}-${i}`}>
                                        <TableCell>{enumLabel(s.status)}</TableCell>
                                        <TableCell align="right">{s.count}</TableCell>
                                        <TableCell align="right">{money(s.claimedAmount)}</TableCell>
                                        <TableCell align="right">{money(s.receivedAmount)}</TableCell>
                                        <TableCell align="right">{money(s.shortfall)}</TableCell>
                                        <TableCell align="right">{money(s.notYetReceivedAmount)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Typography variant="h6" sx={{ mb: 1 }}>Aging (Submitted, not yet Received)</Typography>
                    <TableContainer data-help-section="commercialReports.incentive.aging" component={Paper} sx={{ mb: 2 }} data-testid="incentive-status-aging">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell data-help="commercialReports.incentive.col.bucket">Bucket</TableCell>
                                    <TableCell data-help="commercialReports.incentive.col.count" align="right">Count</TableCell>
                                    <TableCell data-help="commercialReports.incentive.col.claimedAmount" align="right">Claimed Amount</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {report.agingBuckets.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={3} align="center">
                                            <Typography color="text.secondary">No data.</Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                                {report.agingBuckets.map((b, i) => (
                                    <TableRow key={`${b.bucketLabel}-${i}`}>
                                        <TableCell>{b.bucketLabel}</TableCell>
                                        <TableCell align="right">{b.count}</TableCell>
                                        <TableCell align="right">{money(b.claimedAmount)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Typography variant="h6" sx={{ mb: 1 }}>Claims</Typography>
                    <TableContainer data-help-section="commercialReports.incentive.claims" component={Paper} data-testid="incentive-status-rows-table">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell data-help="commercialReports.incentive.col.claimNumber">Claim Number</TableCell>
                                    <TableCell data-help="commercialReports.incentive.col.status">Status</TableCell>
                                    <TableCell data-help="commercialReports.incentive.col.claimed" align="right">Claimed</TableCell>
                                    <TableCell data-help="commercialReports.incentive.col.received" align="right">Received</TableCell>
                                    <TableCell data-help="commercialReports.incentive.col.shortfall" align="right">Shortfall</TableCell>
                                    <TableCell data-help="commercialReports.incentive.col.submitted">Submitted</TableCell>
                                    <TableCell data-help="commercialReports.incentive.col.days" align="right">Days Outstanding</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredRows.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center">
                                            <Typography color="text.secondary">No claims match the current filter.</Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                                {filteredRows.map(row => (
                                    <TableRow key={row.claimId} hover>
                                        <TableCell>{row.claimNumber}</TableCell>
                                        <TableCell>{enumLabel(row.status)}</TableCell>
                                        <TableCell align="right">{money(row.claimedAmount)}</TableCell>
                                        <TableCell align="right">{row.receivedAmount === null ? '—' : money(row.receivedAmount)}</TableCell>
                                        <TableCell align="right">{row.shortfall === null ? '—' : money(row.shortfall)}</TableCell>
                                        <TableCell>{row.submittedDate ? formatDate(row.submittedDate) : "—"}</TableCell>
                                        <TableCell align="right">{row.daysSubmittedNotReceived ?? '—'}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </>
            )}

            {!loading && !report && (
                <Typography color="text.secondary">No data available.</Typography>
            )}
        </Box>
    );
};

export default IncentiveClaimStatusReport;
